import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Download } from 'lucide-react';
import { statusLabel, levelLabel, levelBadgeClass, fmtDate, noticeLine, isCardClosed, isNewChannelImpactCase, channelDisplayLabel, impactDisplayTags } from './conductUtils';

const SEVERITIES = ['MINOR', 'MAJOR', 'GROSS'];

async function downloadFile(fileId, filename) {
  try {
    const res = await api.get(`/conduct/files/${fileId}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (e) {
    toast.error('Failed to download file');
  }
}

function CaseDetailPanel({ caseId, onChanged }) {
  const [item, setItem] = useState(null);
  const [note, setNote] = useState('');
  const [severity, setSeverity] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/conduct/cases/${caseId}`);
      setItem(res.data);
    } catch (e) { toast.error('Failed to load case'); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    try { await fn(); toast.success('Done'); load(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const decide = async () => {
    try {
      const res = await api.post(`/conduct/cases/${caseId}/decide`, { confirmed_severity: severity });
      toast.success(res.data.message === 'Sent to MD for approval' ? 'Sent to MD for approval' : `Card issued: ${levelLabel(res.data.level)}`);
      load(); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  if (!item) return <Loader2 className="animate-spin" />;

  return (
    <Card data-testid={`hr-case-detail-${caseId}`}>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-medium">{item.employee_name || 'Unknown employee'} — {item.category} — {item.proposed_severity}</span>
          <Badge variant="outline">{statusLabel(item.status, item, 'hr')}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">{item.what_happened}</p>
        {isNewChannelImpactCase(item) ? (
          <div className="flex flex-wrap items-center gap-1.5 text-xs" data-testid={`case-channel-impact-${caseId}`}>
            <Badge variant="outline">Channel: {channelDisplayLabel(item)}</Badge>
            {impactDisplayTags(item).map((t) => <Badge key={t} variant="secondary">{t}</Badge>)}
            {item.impact_usd != null && <Badge variant="outline">${Number(item.impact_usd).toLocaleString()}</Badge>}
            {item.impact_text && <span className="text-muted-foreground">{item.impact_text}</span>}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground" data-testid={`case-legacy-impact-${caseId}`}>
            Channel: {item.channel || '—'}{item.what_it_caused ? ` · ${item.what_it_caused}` : ''}{item.impact_text ? ` · ${item.impact_text}` : ''}
          </p>
        )}

        {item.status === 'HR_REVIEW' && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" data-testid={`proceed-btn-${caseId}`}
              onClick={() => act(() => api.post(`/conduct/cases/${caseId}/proceed`))}>Proceed (request statement)</Button>
            <Button size="sm" variant="outline" data-testid={`request-info-btn-${caseId}`}
              onClick={() => act(() => api.post(`/conduct/cases/${caseId}/request-info`, { text: note || 'Please provide more detail' }))}>Request Info</Button>
            <Button size="sm" variant="destructive" data-testid={`dismiss-btn-${caseId}`}
              onClick={() => act(() => api.post(`/conduct/cases/${caseId}/dismiss`, { reason: note || 'No merit' }))}>Dismiss</Button>
          </div>
        )}

        {item.status === 'HR_DECISION' && (
          <div className="flex items-end gap-2">
            <div>
              <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger className="w-40" data-testid={`confirm-severity-select-${caseId}`}><SelectValue placeholder="Confirm severity" /></SelectTrigger>
                <SelectContent>{SEVERITIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button size="sm" disabled={!severity} data-testid={`hr-decide-btn-${caseId}`} onClick={decide}>Confirm &amp; Decide</Button>
            <Button size="sm" variant="destructive" data-testid={`dismiss-btn-${caseId}`}
              onClick={() => act(() => api.post(`/conduct/cases/${caseId}/dismiss`, { reason: note || 'No merit' }))}>Dismiss</Button>
          </div>
        )}

        <Textarea
          className="text-xs"
          placeholder="Note / dismissal reason / info request text"
          data-testid={`hr-note-input-${caseId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button size="sm" variant="secondary" data-testid={`call-note-btn-${caseId}`}
          onClick={() => act(() => api.post(`/conduct/cases/${caseId}/call-notes`, { text: note }))}>Add Call Note</Button>
      </CardContent>
    </Card>
  );
}

function CardActionsPanel({ card, onChanged }) {
  const [managers, setManagers] = useState([]);
  const [reviewerId, setReviewerId] = useState('');
  const [sendBackReason, setSendBackReason] = useState('');

  useEffect(() => {
    if (card.status === 'ACTIVE_CAP' && card.cap?.actions?.length && !card.reviewer_id) {
      api.get('/conduct/hr/managers').then((r) => setManagers(r.data.filter((m) => m.id !== card.reporter_id && m.id !== card.employee_id))).catch(() => {});
    }
  }, [card]);

  const act = async (fn, successMsg) => {
    try { await fn(); toast.success(successMsg || 'Done'); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  const closed = isCardClosed(card.status);

  return (
    <Card data-testid={`hr-card-${card.id}`}>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">{card.employee_name || 'Unknown employee'}</span>
          <div className="flex gap-2">
            <Badge className={levelBadgeClass(card.level)}>{levelLabel(card.level)}</Badge>
            <Badge variant="outline">{statusLabel(card.status, card, 'hr')}</Badge>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Issued {fmtDate(card.issued_at)}</p>
        {noticeLine(card, 'staff') && <p className="text-xs text-amber-700" data-testid={`hr-card-notice-${card.id}`}>{noticeLine(card, 'staff')}</p>}
        {!closed && (
        <div className="flex flex-wrap gap-2">
          {card.cap && card.cap.actions?.length > 0 && !card.cap.approved_by && (
            <Button size="sm" variant="outline" data-testid={`approve-cap-btn-${card.id}`}
              onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/approve`), 'CAP approved')}>Approve CAP</Button>
          )}
          {card.cap && card.cap.approved_by && !(card.cap.reviews?.length) && (
            <div className="flex gap-2 items-center">
              <Input className="h-8 text-xs w-48" placeholder="Reason to send CAP back" value={sendBackReason}
                onChange={(e) => setSendBackReason(e.target.value)} data-testid={`send-back-reason-input-${card.id}`} />
              <Button size="sm" variant="outline" disabled={!sendBackReason.trim()} data-testid={`send-back-cap-btn-${card.id}`}
                onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/send-back`, { reason: sendBackReason }), 'CAP sent back to the manager').then(() => setSendBackReason(''))}>
                Send Back CAP
              </Button>
            </div>
          )}
          {card.cap && card.cap.accepted_at && !card.reviewer_id && (
            <div className="flex gap-2 items-center">
              <Select value={reviewerId} onValueChange={setReviewerId}>
                <SelectTrigger className="w-44" data-testid={`reviewer-select-${card.id}`}><SelectValue placeholder="Assign reviewer" /></SelectTrigger>
                <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" disabled={!reviewerId} data-testid={`assign-reviewer-btn-${card.id}`}
                onClick={() => act(() => api.post(`/conduct/cards/${card.id}/assign-reviewer`, { reviewer_id: reviewerId }), 'Reviewer assigned')}>Assign</Button>
            </div>
          )}
          {card.cap?.reviews?.some((r) => r.cap_met === true) && !card.cap.hr_verified_at && (
            <Button size="sm" variant="outline" data-testid={`verify-cap-btn-${card.id}`}
              onClick={() => act(() => api.post(`/conduct/caps/${card.cap.id}/verify`), 'CAP verified, clean period started')}>Verify CAP Met</Button>
          )}
          {(card.level === 'ORANGE' || card.level === 'YELLOW') && !card.letter_file_id && (
            <Button size="sm" variant="outline" data-testid={`generate-letter-btn-${card.id}`}
              onClick={() => act(() => api.post(`/conduct/cards/${card.id}/letter`, {}), 'Warning letter generated')}>Generate Warning Letter</Button>
          )}
          {card.letter_file_id && (
            <Button size="sm" variant="ghost" data-testid={`download-letter-btn-${card.id}`}
              onClick={() => downloadFile(card.letter_file_id, `warning_letter_${card.id}.pdf`)}>
              <Download className="h-3.5 w-3.5 mr-1" />Download Letter
            </Button>
          )}
          {card.level === 'RED' && !card.show_cause?.file_id && (
            <Button size="sm" variant="outline" data-testid={`send-show-cause-btn-${card.id}`}
              onClick={() => act(() => api.post(`/conduct/cards/${card.id}/show-cause`), 'Show-cause notice sent')}>Send Show-Cause Notice</Button>
          )}
          {card.level === 'RED' && card.show_cause?.file_id && (
            <Button size="sm" variant="ghost" data-testid={`download-show-cause-btn-${card.id}`}
              onClick={() => downloadFile(card.show_cause.file_id, `show_cause_${card.id}.pdf`)}>
              <Download className="h-3.5 w-3.5 mr-1" />Download Notice
            </Button>
          )}
        </div>
        )}
        {card.level === 'RED' && card.show_cause?.reply_due_at && (
          <p className="text-xs text-muted-foreground" data-testid={`hr-reply-due-${card.id}`}>Reply due {fmtDate(card.show_cause.reply_due_at)}</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function HRQueueTab() {
  const [queue, setQueue] = useState([]);
  const [cards, setCards] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, cRes] = await Promise.all([api.get('/conduct/hr/queue'), api.get('/conduct/hr/cards')]);
      setQueue(qRes.data);
      setCards(cRes.data);
    } catch (e) { toast.error('Failed to load HR queue'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-12" data-testid="hr-queue-loading"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="hr-queue-tab">
      <div>
        <h3 className="font-semibold mb-2">Open Cases ({queue.length})</h3>
        <div className="space-y-2">
          {queue.map((c) => (
            <div key={c.id}>
              <Card className="cursor-pointer hover:border-primary" data-testid={`hr-queue-case-${c.id}`} onClick={() => setSelected(selected === c.id ? null : c.id)}>
                <CardContent className="pt-4 flex items-center justify-between">
                  <span className="text-sm">{c.employee_name || 'Unknown employee'} — {c.category} — {c.proposed_severity}</span>
                  <Badge variant="outline">{statusLabel(c.status, c, 'hr')}</Badge>
                </CardContent>
              </Card>
              {selected === c.id && <div className="mt-2"><CaseDetailPanel caseId={c.id} onChanged={load} /></div>}
            </div>
          ))}
          {queue.length === 0 && <p className="text-sm text-muted-foreground" data-testid="hr-queue-empty">No open cases.</p>}
        </div>
      </div>

      <div>
        <h3 className="font-semibold mb-2">Cards</h3>
        <div className="space-y-2">
          {cards.map((card) => <CardActionsPanel key={card.id} card={card} onChanged={load} />)}
          {cards.length === 0 && <p className="text-sm text-muted-foreground" data-testid="hr-cards-empty">No conduct cards yet.</p>}
        </div>
      </div>
    </div>
  );
}
